import { Address } from '../address';
import { Brand } from '../brand';
import { CapabilityListItem } from '../capability';
import { CarSize } from '../cars';
import { OptionsTypeInteger } from '../core';
import { Currency } from '../currency';
import { SalesItemCategory } from '../enums';
import { ServiceCategory } from '../serviceCategory';
import { UnableToCompleteReason } from '../unableToComplete';
import { UserGroupMinimal } from '../userGroup';

export type Money = {
  amount: number;
  currency: Currency;
};

export interface SalesItemListItem {
  id: number;
  name: string;
  nameInternal: string;
  price: Money;
  carSizes: CarSize[];
  slug: string;
  category: string;
  description: string;
  shortDescription: string;
  isActive: boolean;
  salesItemType: number;
  uiSortOrder: number;
  vatPercentage: number;
}

export type SalesItemPriceProps = {
  grossPrice: Money;
  netPrice: Money;
  vatRate: number;
  vatAmount: Money;
};

export type SalesItemPartnerDetails = SalesItemPriceProps & {
  id: number;
  slug: string;
  name: string;
  nameInternal: string;
  description: string;
  shortDescription: string;
  workerName: string;
  workerDescription: string;
  serviceTimeInSeconds: number;
  freelancerPayout: Money;
  employerPayout: Money;
  serviceCategory: ServiceCategory;
};

export type SalesItemPartnerList = SalesItemPriceProps & {
  id: number;
  slug: string;
  name: string;
  nameInternal: string;
  description: string;
  shortDescription: string;
  workerName: string;
  workerDescription: string;
  serviceTimeInSeconds: number;
  freelancerPayout: Money;
  employerPayout: Money;
};

export type SalesItemFilters = {
  isActive?: boolean;
  currencies?: Currency[];
  serviceCategoryIds?: number[];
  type?: number[];
  serviceDepartmentIds?: number[];

  // The sales items that are available to add in the service department which is not already added to the sales item
  // catalog of the service department
  availableToAddInServiceDepartmentId?: number;
};

export interface SalesItemBookingCategory {
  id: number;
  slug: string;
}

export interface MinimalSalesItem {
  id: number;
  name: string;
  category: string;
  price?: Money;
}

export type SalesItemV2 = {
  salesItemId: number;
  name: string;
  description: string;
  shortDescription: string;
  price: Money;
  uiSortOrder: number;
  bookingCategorySlug: string | null;
  mutuallyExclusiveSalesItemIds: number[];
  isWheelPickupRequired: boolean;
  isAddonsAvailable: boolean;
};

export type SalesItemTag = {
  id: number;
  name: string;
};

export type TierHotelPickupSalesItemOption = {
  requiresPickupAddressSelection: boolean;
} & SalesItemV2;

export type SalesItemsCar = {
  licensePlate: LicensePlateInfo;
  make: string;
  model: string;
  salesItems: SalesItemV2[];
};

export type AvailableSalesItemsForBooking = {
  cars: SalesItemsCar[];
};

export interface ConflictingSalesItem {
  salesItemId: number;
  name: string;
}

export interface UnavailableSalesItem {
  salesItemId: number;
  conflictingSelectedSalesItems: ConflictingSalesItem[];
}

export enum BOOKING_TYPE {
  NORMAL = 'NORMAL',
  CANCEL_TIER_HOTEL = 'CANCEL_TIER_HOTEL',
  RECALL = 'RECALL'
}

export enum SALES_ITEM_TYPE {
  PRIMARY = 'PRIMARY',
  ADDON = 'ADDON',
  TIRE_HOTEL_PICKUP = 'TIRE_HOTEL_PICKUP',
  TIRE_HOTEL_CANCEL = 'TIRE_HOTEL_CANCEL'
}

export enum SALES_ITEM_TYPE_INTEGER {
  PRIMARY = 0,
  ADDON = 1,
  EXTRA_CHARGE = 2,
  LEGACY_BUNDLE = 3,
  INTERNAL = 4
}

export type LicensePlateInfo = {
  number: string;
  countryCode: string;
};

export interface ShoppingCartSalesItem {
  salesItemId: number;
  name: string;
  price: Money;
}

export interface ShoppingCartMembershipDiscount {
  membershipDiscountId: number;
  salesItemId: number;
  name: string;
  discount: Money;
}

export interface SalesItemsFilter {
  type: number[];
  currencies?: Currency[];
  categories?: SalesItemCategory[];
}

export interface ShoppingCartCar {
  licensePlate: LicensePlateInfo;
  make: string;
  model: string;
  salesItems: ShoppingCartSalesItem[];
}

export interface ShoppingCartCouponDiscount {
  couponId: number;
  name: string;
  discount: Money;
}

export type ShoppingCart = {
  cars: ShoppingCartCar[];
  couponDiscounts: ShoppingCartCouponDiscount[];
  membershipDiscounts: ShoppingCartMembershipDiscount[];
  totalPrice: Money;
  deliveryWindowFee: Money | null;
};

export interface RecallSalesItem {
  salesItemId: number;
  name: string;
  price: Money;
  isWheelPickupRequired: boolean;
  isAddonsAvailable: boolean;
  bookingCategorySlug: string | null;
  // This is BOOKING_TYPES enum in frontend -.-
  isPrimary: boolean;
  isAddon: boolean;
  isTireHotelPickup: boolean;
  isTireHotelCancel: boolean;
  // Todo get this from api
  // metaData?: SalesItemMetaData;
}

export type RecallCars = {
  licensePlate: LicensePlateInfo;
  make: string;
  model: string;
  salesItems: RecallSalesItem[];
  hasActiveTireHotelContract: boolean;
};

export type RecallBooking = {
  bookingId: number;
  address: Address;
  deliveryWindowStartsAt: string;
  deliveryWindowEndsAt: string;
  cars: RecallCars[];
  userFirstName: string;
  couponDiscounts: ShoppingCartCouponDiscount[];
  membershipDiscounts: ShoppingCartMembershipDiscount[];
  totalPrice: Money;
  deliveryWindowFee: Money | null;
  brand: Brand;
};

export type SelectedSalesItemsCarGrouped = {
  licensePlate: LicensePlateInfo;
  selectedSalesItemIds: number[];
  pickupAddressId?: number;
};

export type SelectedSalesItemsCar = {
  licensePlate: LicensePlateInfo;
  salesItemId: number;
  bookingCategorySlug: string | null;
  type: SALES_ITEM_TYPE;
  metaData?: SalesItemMetaData;
  isWheelPickupRequired: boolean;
  isAddonsAvailable: boolean;
};

export type SalesItemMetaData = {
  selectedTierHotelPickUpAddress: Address;
};

export type SalesItemOption = {
  id: number;
  name: string;
};

export type SalesItemProps = {
  id: number;
  slug: string;
  name: string;
  nameInternal: string;
  description: string;
  shortDescription: string;
  workerName: string;
  workerDescription: string;
  isActive: boolean;
  price: Money;
  category: string;
  vatPercentage: number;
  serviceTime: number;
  commissionLevel2Payment: number;
  commissionLevel1Payment: number;
  carSizes: CarSize[];
  serviceCategory: CarSize;
  bookingCategory: SalesItemBookingCategory | null;
  tags: SalesItemTag[];
  salesItemType: OptionsTypeInteger;
  uiSortOrder: number;
  addons: SalesItemListItem[];
  incompatibles: SalesItemListItem[];
  isPrivate: boolean;
  userGroups: UserGroupMinimal[];
  unableToCompleteReasons: UnableToCompleteReason[];
  carWheelMeasurementRequirement: string;
  capabilities: CapabilityListItem[];
};

export type SalesItemType = Omit<
  SalesItemProps,
  | 'serviceCategory'
  | 'carSizes'
  | 'addons'
  | 'incompatibles'
  | 'userGroups'
  | 'unableToCompleteReasons'
  | 'tags'
  | 'capabilities'
>;

export interface SalesItemUpdatedType extends SalesItemType {
  serviceCategoryId: number;
  carSizeIds: number[];
  addonIds: number[];
  incompatibleIds: number[];
  userGroupIds: number[];
  unableToCompleteReasonIds: number[];
  tagIds: number[];
  capabilityIds: number[];
}

export type SalesItemUpdateInputType = Omit<SalesItemUpdatedType, 'salesItemType' | 'bookingCategory'> & {
  bookingCategoryId?: number | null;
  salesItemType: number;
};

export type SalesItemCreateInputType = Omit<SalesItemUpdateInputType, 'id'>;

export const CarWheelMeasurementRequirements = {
  None: 'NONE',
  Mounted: 'MOUNTED',
  All: 'ALL'
} as const;

export const carWheelMeasurementRequirementValues = Object.values(CarWheelMeasurementRequirements);
